import { Injectable } from '@angular/core';

export const LUMEN_THEME_PREFERENCE_KEY = 'lumen.themePreference';

export type ThemePreference = 'SYSTEM' | 'LIGHT' | 'DARK';

export function normalizeThemePreference(value: unknown): ThemePreference {
  if (value === 'LIGHT' || value === 'DARK' || value === 'SYSTEM') {
    return value;
  }
  return 'SYSTEM';
}

/**
 * Applies the Light / Dark / System appearance to <html> and caches it for the
 * first-paint script in index.html.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  constructor() {
    this.apply(this.readCached());
  }

  apply(preference: ThemePreference): void {
    const normalized = normalizeThemePreference(preference);
    const root = document.documentElement;
    if (normalized === 'LIGHT') {
      root.setAttribute('data-theme', 'light');
    } else if (normalized === 'DARK') {
      root.setAttribute('data-theme', 'dark');
    } else {
      root.removeAttribute('data-theme');
    }
  }

  persist(preference: ThemePreference): void {
    const normalized = normalizeThemePreference(preference);
    try {
      localStorage.setItem(LUMEN_THEME_PREFERENCE_KEY, normalized);
    } catch {
      // Ignore quota / private-mode failures; the DOM attribute still applies.
    }
  }

  readCached(): ThemePreference {
    try {
      return normalizeThemePreference(localStorage.getItem(LUMEN_THEME_PREFERENCE_KEY));
    } catch {
      return 'SYSTEM';
    }
  }

  hydrateFromSettings(preference?: ThemePreference | null): void {
    const normalized = normalizeThemePreference(preference);
    this.apply(normalized);
    this.persist(normalized);
  }
}
